import mixins from 'vue-typed-mixins';
import { PropType } from 'vue';
import _ from 'lodash';

import {
  E借入金条件,
  E勘定科目カテゴリ,
  E回収支払条件,
  資金繰り経常収支Config,
  資金繰り財務収支Config,
} from '@/openapi/user';

import AlertMixin from '@/mixins/AlertMixin';
import MoneyUnitLabelMixin from '@/entries/user/mixins/MoneyUnitLabelMixin';
import RoutesMixin from '@/entries/user/mixins/RoutesMixin';
import 事業所Mixin from '@/entries/user/mixins/事業所Mixin';
import 計画書Mixin from '@/entries/user/mixins/計画書Mixin';

import AppFormatUtility from '@/classes/common/AppFormatUtility';
import { MONTHS_OF_YEAR } from '@/classes/common/Constants';
import 資金繰りService, { ConfigListItem, 資金繰り予定 } from '@/classes/service/計画/資金繰りService';

export default mixins(AlertMixin, MoneyUnitLabelMixin, RoutesMixin, 事業所Mixin, 計画書Mixin).extend({
  name: 'C資金繰り設定',

  props: {
    service: {
      type: Object as PropType<資金繰りService>,
      required: true,
    },
    資金繰り予定: {
      type: Object as PropType<資金繰り予定>,
      default: null,
    },
  },

  data(): {
    dialog: {
      item: ConfigListItem | null;
      経常収支Config: 資金繰り経常収支Config | null;
      財務収支Config: 資金繰り財務収支Config | null;
    };
  } {
    return {
      dialog: {
        item: null,
        経常収支Config: null,
        財務収支Config: null,
      },
    };
  },

  computed: {
    勘定科目Selects(): { label: string; value: number }[] {
      const 損益大分類s: string[] = [];
      const 製造原価大分類s: string[] = [];

      switch (this.dialog.item?.経常ConfigKey) {
        case '営業収入s':
          損益大分類s.push('売上高');
          break;

        case '営業外収入s':
          損益大分類s.push('営業外収益', '特別利益');
          break;

        case '営業支出s':
          損益大分類s.push('売上原価', '販売管理費', '法人税等');
          製造原価大分類s.push('材料費', '労務費', '製造経費');
          break;

        case '営業外支出s':
          損益大分類s.push('営業外費用', '特別損失');
          break;
      }

      if (製造原価大分類s.length === 0) {
        return this.service.get勘定科目s
          .filter((勘定科目) => 勘定科目.cカテゴリ === E勘定科目カテゴリ.損益 && 損益大分類s.includes(勘定科目.c大分類))
          .map((勘定科目) => ({ label: 勘定科目.c勘定科目, value: 勘定科目.pk }));
      }

      return this.service.get勘定科目s
        .filter(
          (勘定科目) =>
            (勘定科目.cカテゴリ === E勘定科目カテゴリ.損益 && 損益大分類s.includes(勘定科目.c大分類)) ||
            (勘定科目.cカテゴリ === E勘定科目カテゴリ.製造原価 && 製造原価大分類s.includes(勘定科目.c大分類))
        )
        .sort((a, b) => (a.cカテゴリ !== b.cカテゴリ ? a.cカテゴリ - b.cカテゴリ : a.c並び順 - b.c並び順))
        .map((勘定科目) => ({ label: 勘定科目.c大分類 + '/' + 勘定科目.c勘定科目, value: 勘定科目.pk }));
    },
    回収支払条件s(): E回収支払条件[] {
      switch (this.dialog.item?.経常ConfigKey) {
        case '営業収入s':
        case '営業外収入s':
          return [
            E回収支払条件.回収0,
            E回収支払条件.回収1,
            E回収支払条件.回収2,
            E回収支払条件.回収3,
            E回収支払条件.回収4,
            E回収支払条件.回収5,
            E回収支払条件.回収6,
          ];
        case '営業支出s':
        case '営業外支出s':
          return [
            E回収支払条件.支払0,
            E回収支払条件.支払1,
            E回収支払条件.支払2,
            E回収支払条件.支払3,
            E回収支払条件.支払4,
            E回収支払条件.支払5,
            E回収支払条件.支払6,
          ];
      }
      return [];
    },
    借入金条件s(): E借入金条件[] {
      switch (this.dialog.item?.財務ConfigKey) {
        case '財務収入s':
          return [E借入金条件.入金短期, E借入金条件.入金長期, E借入金条件.入金役員];
        case '財務支出s':
          return [E借入金条件.返済短期, E借入金条件.返済長期, E借入金条件.返済役員];
      }
      return [];
    },
  },

  watch: {
    '資金繰り予定.開始月': {
      async handler(_, old: number): Promise<void> {
        const ymList = this.service.ymList;
        const min = ymList[0]; // 0期期首月
        const max = ymList[MONTHS_OF_YEAR]; // 1期期首月
        const 開始月 = this.資金繰り予定.開始月!;
        if (開始月 < min || max < 開始月) {
          const from = AppFormatUtility.format年月(min);
          const to = AppFormatUtility.format年月(max);
          window.alert('開始月は' + from + 'から' + to + 'を指定して下さい');

          this.資金繰り予定.開始月 = min <= old && old <= max ? old : min;
          return;
        }

        this.service.cデータ資金繰り!.config.開始月 = String(開始月);
        await this.$_save();
      },
    },
  },

  methods: {
    open経常収支Dialog(item: ConfigListItem): void {
      this.dialog.item = item;

      this.dialog.経常収支Config = _.cloneDeep(item.経常収支Config!);
      this.dialog.財務収支Config = null;
    },
    open財務収支Dialog(item: ConfigListItem): void {
      this.dialog.item = item;

      this.dialog.経常収支Config = null;
      this.dialog.財務収支Config = _.cloneDeep(item.財務収支Config!);
    },
    clear経常収支Config(): void {
      this.dialog.経常収支Config = this.service.createEmpty経常収支Config();
    },
    clear財務収支Config(): void {
      this.dialog.財務収支Config = this.service.createEmpty財務収支Config();
    },
    async set経常収支Config(): Promise<void> {
      const config = this.dialog.item!.経常収支Config!;
      Object.assign(config, this.dialog.経常収支Config);

      const values = Array.from({ length: 5 }).map((_, i) => this.service.get勘定科目名(config.勘定科目Pks[i]));
      this.$_updateCellsValue([config.label, ...values, config.回収支払条件 ?? '']);

      this.dialog.item = null;
      await this.$_save();
    },
    async set財務収支Config(): Promise<void> {
      const config = this.dialog.item!.財務収支Config!;
      Object.assign(config, this.dialog.財務収支Config);

      const values = new Array(5).fill('');
      this.$_updateCellsValue([config.label, ...values, config.借入金条件 ?? '']);

      this.dialog.item = null;
      await this.$_save();
    },

    $_updateCellsValue(values: string[]): void {
      const cells = this.dialog.item!.cells;
      cells.slice(cells.length - values.length).forEach((cell, i) => (cell.value = values[i]));
    },
    async $_save(): Promise<void> {
      const cデータ資金繰り = this.service.orgCデータ資金繰り!;
      cデータ資金繰り.config = _.cloneDeep(this.service.cデータ資金繰り!.config);

      const response = await this.service.update(this.資金繰り予定.データpk!, cデータ資金繰り);
      this.isValidationErrorResponse(response);
    },
  },
});
